import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Avatar, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tooltip, CircularProgress, Stack, Switch,
  Alert, Autocomplete, Divider,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon, PersonAdd as AddIcon, Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ROLE_LABEL = { clinic_admin: 'Admin', provider: 'Provider', staff: 'Staff' };
// Admins who run this clinic from the level above rather than as one of its members.
const VIA_LABEL = { region: 'Region admin', organization: 'Org admin' };

export default function AdministratorList() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '' });
  const [saving, setSaving] = useState(false);
  // Someone already on the team, picked from the dropdown in the Add dialog.
  const [picked, setPicked] = useState(null);

  const orgName = user?.practice_name || 'this clinic';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/members`);
      setMembers(res.data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load the team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAdmin = async (member, grant) => {
    setBusyId(member.id);
    try {
      const res = await axios.put(
        `${API_URL}/api/v1/admin/members/${member.id}/clinic-admin`, { grant });
      toast.success(res.data?.message || 'Updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update their access');
    } finally {
      setBusyId(null);
    }
  };

  // An administrator can also practise here. Turning this on gives them a provider
  // role beside their admin one: they appear to patients as a doctor at this clinic and
  // can switch into their own provider view from the portal switcher.
  const setProvider = async (member, grant) => {
    setBusyId(member.id);
    try {
      const res = await axios.put(
        `${API_URL}/api/v1/admin/members/${member.id}/provider`, { grant });
      toast.success(res.data?.message || 'Updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update their provider role');
    } finally {
      setBusyId(null);
    }
  };

  const closeAdd = () => {
    if (saving) return;
    setInviteOpen(false);
    setPicked(null);
  };

  // The quick path: they already work here, so no invitation or new password — the
  // admin role is added to the account they have, exactly as the table's switch does.
  const promotePicked = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/v1/admin/members/${picked.id}/clinic-admin`, { grant: true });
      toast.success(res.data?.message || 'Administrator added');
      setInviteOpen(false);
      setPicked(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not make them an administrator');
    } finally {
      setSaving(false);
    }
  };

  const invite = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/admin/administrators`, {
        full_name: form.full_name,
        email: form.email,
      });
      toast.success(res.data?.message || 'Administrator added');
      setInviteOpen(false);
      setForm({ full_name: '', email: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add an administrator');
    } finally {
      setSaving(false);
    }
  };

  const resend = async (member) => {
    setBusyId(member.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/v1/admin/administrators/${member.id}/resend-invite`);
      toast.success(res.data?.message || 'Invite re-sent');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend the invite');
    } finally {
      setBusyId(null);
    }
  };

  const admins = members.filter((m) => m.is_clinic_admin);
  // Everyone at this office who could be made an administrator.
  const candidates = members.filter((m) => !m.is_clinic_admin);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><AdminIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Administrators</Typography>
          <Typography variant="body2" color="text.secondary">
            Who can manage {orgName} — its providers, staff, patients and settings
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }}
          onClick={() => setInviteOpen(true)}>
          Add administrator
        </Button>
      </Box>

      {!loading && admins.length === 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You're the only administrator here. Adding a second one means nobody gets
          locked out if you lose access to your account.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Turn on <strong>Administrator</strong> for anyone already on your team — they
            keep the role they have now. Turn on <strong>Provider</strong> for an administrator
            who also sees patients. Use <strong>Add administrator</strong> only for
            somebody who isn't here yet.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Administrator</TableCell>
                  <TableCell align="center">Provider</TableCell>
                  <TableCell align="right">Invite</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7}>Loading…</TableCell></TableRow>
                ) : members.length === 0 ? (
                  <TableRow><TableCell colSpan={7}>Nobody here yet.</TableCell></TableRow>
                ) : members.map((m) => {
                  const isMe = String(m.id) === String(user?.sub || user?.id);
                  const lastAdmin = m.is_clinic_admin && admins.length === 1;
                  const lockedReason = isMe
                    ? "You can't remove your own administrator access — ask a colleague."
                    : lastAdmin
                      ? 'This is the only administrator. Add another one first.'
                      : null;
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>
                        {/* component="div": a Chip renders a <div>, which cannot
                            legally sit inside the <p> Typography defaults to. */}
                        <Typography component="div" fontWeight={600}>
                          {m.full_name || m.email}
                          {isMe && <Chip size="small" label="You" sx={{ ml: 1 }} />}
                        </Typography>
                        {!m.full_name && (
                          <Typography variant="caption" color="text.secondary">
                            name not set yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {m.admin_via && (
                            <Chip size="small" label={VIA_LABEL[m.admin_via]} color="info" />
                          )}
                          {m.roles.map((r) => (
                            <Chip key={r} size="small" label={ROLE_LABEL[r] || r}
                              color={r === 'clinic_admin' ? 'warning' : 'default'} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {m.invite_pending ? (
                          <Chip size="small" label="Pending invite" color="warning" />
                        ) : (
                          <Chip size="small" label={m.is_active ? 'Active' : 'Inactive'}
                            color={m.is_active ? 'success' : 'default'} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={m.locked || (m.admin_via && !m.roles.includes('clinic_admin'))
                          ? `Administers this clinic through their ${m.admin_via === 'region' ? 'region' : 'organisation'} — managed on that level's Administrators screen`
                          : lockedReason
                          || (m.is_clinic_admin
                            ? 'Remove administrator access'
                            : 'Let them administer this clinic — their current role is kept')}>
                          <span>
                            <Switch
                              size="small"
                              checked={m.is_clinic_admin}
                              disabled={busyId === m.id || Boolean(m.is_clinic_admin && lockedReason)
                                || Boolean(m.admin_via && !m.roles.includes('clinic_admin')) || Boolean(m.locked)}
                              onChange={(e) => setAdmin(m, e.target.checked)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {m.is_clinic_admin ? (
                          <Tooltip title={m.locked || (m.roles.includes('provider')
                            ? 'Stop practising here — they stay an administrator'
                            : 'Let them see patients and run sessions as a provider too')}>
                            <span>
                              <Switch
                                size="small"
                                color="secondary"
                                checked={m.roles.includes('provider')}
                                disabled={busyId === m.id || Boolean(m.locked)}
                                onChange={(e) => setProvider(m, e.target.checked)}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          // Not an administrator: their role here is managed on the
                          // Providers and Staff screens.
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {m.invite_pending && m.is_clinic_admin ? (
                          <Button size="small" startIcon={<SendIcon fontSize="small" />}
                            disabled={busyId === m.id} onClick={() => resend(m)}>
                            {busyId === m.id ? 'Sending…' : 'Resend invite'}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={inviteOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add an administrator to {orgName}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
            Someone already on your team
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pick a provider or staff member. They keep the role they have and sign in as
            they do now.
          </Typography>
          <Autocomplete
            options={candidates}
            value={picked}
            onChange={(e, v) => setPicked(v)}
            getOptionLabel={(m) => m.full_name || m.email}
            // Search matches name OR email, so either is enough to find someone.
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              return q ? opts.filter((m) => `${m.full_name || ''} ${m.email}`.toLowerCase().includes(q)) : opts;
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={candidates.length ? 'Nobody matches that search' : 'Everyone here is already an administrator'}
            renderOption={(props, m) => (
              <Box component="li" {...props} key={m.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 14, bgcolor: 'primary.main' }}>
                  {(m.full_name || m.email).charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{m.full_name || m.email}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{m.email}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {m.roles.map((r) => <Chip key={r} size="small" label={ROLE_LABEL[r] || r} />)}
                  {!m.is_active && <Chip size="small" label="Inactive" />}
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search by name or email" size="small" />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button variant="contained" onClick={promotePicked} disabled={saving || !picked}
              startIcon={saving && picked ? <CircularProgress size={16} color="inherit" /> : <AdminIcon />}>
              Make administrator
            </Button>
          </Box>

          <Divider sx={{ my: 2.5 }}>or</Divider>

          <Typography variant="subtitle2" fontWeight={700}>Someone new</Typography>
          <Typography variant="body2" color="text.secondary">
            They'll get a single-use link, valid 48 hours, to set their own password. If
            the email already has a ScribeLive account, administrator access is added to
            it and they keep their existing password.
          </Typography>
          <TextField fullWidth label="Full name" margin="normal" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <TextField fullWidth label="Email" margin="normal" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAdd} disabled={saving}>Cancel</Button>
          <Button variant="outlined" onClick={invite}
            disabled={saving || !form.email.trim() || !form.full_name.trim()}
            startIcon={saving && !picked ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
            {saving && !picked ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
